import React, { useState, useEffect } from 'react';
import { doc, setDoc, getDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { useAuth } from '../../context/AuthContext';
import TakePictureCam from './TakePictureCam';

const T = {
  en: {
    loading:      'Loading logo...',
    imgLoading:   'Loading image...',
    notLoggedIn:  'You must be signed in to manage the logo',
    title:        'Company Logo',
    lastUpdated:  'Last updated:',
    notAvailable: 'N/A',
    notAuth:      'User not authenticated',
    saveError:    'Error saving logo: ',
    deleteError:  'Error deleting logo: ',
  },
  es: {
    loading:      'Cargando logo...',
    imgLoading:   'Cargando imagen...',
    notLoggedIn:  'Debe iniciar sesión para gestionar el logo',
    title:        'Logo de la Empresa',
    lastUpdated:  'Última actualización:',
    notAvailable: 'N/A',
    notAuth:      'Usuario no autenticado',
    saveError:    'Error al guardar el logo: ',
    deleteError:  'Error al eliminar el logo: ',
  },
};

function ImageLoadingOverlay({ label }) {
  return (
    <div style={{
      position:        'absolute',
      inset:           0,
      display:         'flex',
      flexDirection:   'column',
      alignItems:      'center',
      justifyContent:  'center',
      backgroundColor: 'rgba(255,255,255,0.75)',
      borderRadius:    '8px',
      zIndex:          10,
      gap:             '10px',
    }}>
      <style>{`
        @keyframes _spin { to { transform: rotate(360deg); } }
        ._logo-spinner {
          width: 36px; height: 36px;
          border: 4px solid #e0e0e0;
          border-top-color: #555;
          border-radius: 50%;
          animation: _spin 0.8s linear infinite;
        }
      `}</style>
      <div className="_logo-spinner" />
      <span style={{ fontSize: '13px', color: '#555' }}>{label}</span>
    </div>
  );
}

export default function Logo() {
  const { currentUser, language } = useAuth();
  const t = language === 'es' ? T.es : T.en;

  const [logoData,   setLogoData]   = useState(null);
  const [loading,    setLoading]    = useState(true);
  const [imgLoading, setImgLoading] = useState(false);

  useEffect(() => {
    if (!logoData?.Url) { setImgLoading(false); return; }
    setImgLoading(true);
    const img = new Image();
    img.src = logoData.Url;
    img.onload  = () => setImgLoading(false);
    img.onerror = () => setImgLoading(false);
  }, [logoData?.Url]);

  useEffect(() => {
    const loadLogo = async () => {
      if (!currentUser?.uid) { setLoading(false); return; }
      try {
        const logoDocRef = doc(db, 'Logo', currentUser.uid);
        const logoDoc    = await getDoc(logoDocRef);
        if (logoDoc.exists()) setLogoData(logoDoc.data());
      } catch (error) {
        console.error('Error loading logo:', error);
      } finally {
        setLoading(false);
      }
    };
    loadLogo();
  }, [currentUser]);

  const handleImageUploaded = async (imgObj) => {
    if (!currentUser?.uid) { alert(t.notAuth); return; }
    try {
      const logoDocRef   = doc(db, 'Logo', currentUser.uid);
      const logoDocument = {
        Url:       imgObj.Url,
        userId:    currentUser.uid,
        updatedAt: serverTimestamp(),
        createdAt: logoData?.createdAt || serverTimestamp(),
      };
      await setDoc(logoDocRef, logoDocument, { merge: true });
      setLogoData(logoDocument);
    } catch (error) {
      console.error('Error saving logo:', error);
      alert(t.saveError + error.message);
    }
  };

  const handleImageDeleted = async () => {
    if (!currentUser?.uid) return;
    try {
      const logoDocRef = doc(db, 'Logo', currentUser.uid);
      await deleteDoc(logoDocRef);
      setLogoData(null);
    } catch (error) {
      console.error('Error deleting logo:', error);
      alert(t.deleteError + error.message);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>{t.loading}</p>
      </div>
    );
  }

  if (!currentUser) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <p>{t.notLoggedIn}</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <h3 style={{ marginBottom: '20px' }}>{t.title}</h3>

      <div style={{
        position:       'relative',
        display:        'flex',
        justifyContent: 'center',
        width:          '100%',
      }}>
        <TakePictureCam
          DocID={currentUser.uid}
          NoImg="Logo"
          detalle={logoData}
          onImageUploaded={handleImageUploaded}
          onImageDeleted={handleImageDeleted}
          storagePath="Logo"
        />

        {imgLoading && <ImageLoadingOverlay label={t.imgLoading} />}
      </div>

      {logoData && (
        <div style={{
          marginTop:       '20px',
          padding:         '15px',
          backgroundColor: '#f8f9fa',
          borderRadius:    '8px',
          fontSize:        '14px',
          color:           '#6c757d',
        }}>
          <p>
            <strong>{t.lastUpdated}</strong>{' '}
            {logoData.updatedAt?.toDate?.()?.toLocaleString() || t.notAvailable}
          </p>
        </div>
      )}
    </div>
  );
}